import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { LoadingState } from '@/components/ui/states';
import { ProtectedRoute, MemberGuard, OnboardingGuard, ConceptAdminGuard, SystemAdminGuard } from '@/components/guards/guards';
import { useAuth } from '@/contexts/AuthContext';

// Route-prefix portals behind guards in one SPA (docs/03). Pages lazy-load
// inside a persistent AppShell per portal.
const Login = lazy(() => import('@/pages/auth/Login'));
const ForgotPassword = lazy(() => import('@/pages/auth/ForgotPassword'));
const ResetPassword = lazy(() => import('@/pages/auth/ResetPassword'));
const InviteAccept = lazy(() => import('@/pages/auth/InviteAccept'));
const MembershipUnavailable = lazy(() => import('@/pages/auth/MembershipUnavailable'));
const NotFound = lazy(() => import('@/pages/auth/NotFound'));
const Onboarding = lazy(() => import('@/pages/auth/onboarding/Onboarding'));
const MemberPortal = lazy(() => import('@/pages/member/MemberPortal'));
const ConceptPortal = lazy(() => import('@/pages/concept/ConceptPortal'));
const AdminPortal = lazy(() => import('@/pages/admin/AdminPortal'));

function RoleHome() {
  const { me, homePath } = useAuth();
  if (!me) return null;
  const home = homePath(me);
  return home === '/' ? null : <Navigate to={home} replace />;
}

export default function App() {
  return (
    <Suspense fallback={<LoadingState />}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />
        <Route path="/invite/:token" element={<InviteAccept />} />
        <Route path="/not-found" element={<NotFound />} />

        <Route element={<ProtectedRoute />}>
          <Route path="/membership-unavailable" element={<MembershipUnavailable />} />
          <Route element={<OnboardingGuard />}>
            <Route path="/onboarding" element={<Onboarding />} />
          </Route>

          <Route element={<MemberGuard />}>
            <Route path="/*" element={<MemberPortal />} />
          </Route>
          <Route element={<ConceptAdminGuard />}>
            <Route path="/concept/*" element={<ConceptPortal />} />
          </Route>
          <Route element={<SystemAdminGuard />}>
            <Route path="/admin/*" element={<AdminPortal />} />
          </Route>
        </Route>

        <Route path="*" element={<RoleHome />} />
      </Routes>
    </Suspense>
  );
}
